import { InsurancePolicy } from '@advisory-report/src/types/AdvisoryReport';
import { FC } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  useBoolean,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import AnvaPackageItemEditModalBody from '@flows/src/components/flow-products/quotes/basket/AnvaPackageItemEditModalBody';
import PackageEditModalFooter from '@advisory-report/src/features/creation-flow/components/insurance-policies/current-insurances/packages/PackageEditModalFooter';
import { usePostRecalculateSimulation } from '@flows/src/api/anva-flows/simulation/postRecalculateSimulation';
import { SchemaRecalculateAnvaSimulationCmd } from 'wg-api-sdk';

interface Props {
  partyId: string;
  policy: InsurancePolicy;
  sessionId: string;
  isOpen: boolean;
  onClose: () => void;
}

const AnvaSimulationPolicyEditModal: FC<Props> = ({
  partyId,
  policy,
  isOpen,
  onClose,
  sessionId,
}) => {
  const { t } = useTranslation();
  const { mutateAsync: recalculate } = usePostRecalculateSimulation();
  const methods = useForm({
    defaultValues: {
      payment_interval: policy.payment_interval,
    },
  });
  const [isLoading, setIsLoading] = useBoolean();

  const handleSubmit = methods.handleSubmit(async (values) => {
    try {
      setIsLoading.on();
      await recalculate({
        sessionId,
        payload: {
          insurance_policy_id: policy.id,
          payment_interval:
            values.payment_interval as SchemaRecalculateAnvaSimulationCmd['payment_interval'],
        },
      });
      onClose();
    } finally {
      setIsLoading.off();
    }
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(
            `ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_EDIT_TITLE`,
          )}
        </ModalHeader>
        <ModalCloseButton />
        <FormProvider {...methods}>
          <AnvaPackageItemEditModalBody
            hiddenFields={['anva_collection_method_code', 'start_date']}
            type="POLICY"
          />
        </FormProvider>
        <PackageEditModalFooter
          onSubmit={handleSubmit}
          isLoading={isLoading}
          onClose={onClose}
          isDisabled={!methods.formState.isDirty}
        />
      </ModalContent>
    </Modal>
  );
};

export default AnvaSimulationPolicyEditModal;
